'use strict';

namespace Search {

    class DateRangePicker {
        // Attr
        startDate: string;
        endDate: string;
        minDate: string;
        maxDate: string;
        enabledDates: any = {};
        select: () => void;
        show: boolean;
        durations: number[] = [];
        oneWay: boolean;

        private elements: any;
        private datePicker: any = null;
        private nextDateToSet: string = 'start';
        private localStartDate: Date | undefined = undefined;
        private localEndDate: Date | undefined = undefined;
        private shadowStartDate: Date | undefined = undefined;
        private shadowEndDate: Date | undefined = undefined;
        private duration: Number = 0;
        private html: JQuery;

        constructor (
            private $scope: ng.IScope,
            private $timeout: ng.ITimeoutService,
            private dateUtil: any,
            private animationUtil: any
        ) {
            this.html = $('html');
        }

        init (elements): void {
            this.elements = elements;

            this.$scope.$watch('drp.show', (val) => {
                if (val != null) {
                    if (val) {
                        this.open();
                    } else {
                        this.close();
                    }
                }
            });

            // set shadow dates on hover
            // (disabled for touch as it requires the user to press twice to select a date)
            if (!TRPCONFIG.IsTouchDevice) {
                this.elements.datepickerEl.on('mouseover.datetangepicker', 'td.drp-date', (event) => {
                    let $cell = $(event.currentTarget),
                        date = this.getDateFromCalendarCell($cell);

                    if ($cell.hasClass('ui-datepicker-unselectable')) {
                        this.resetShadowDates();
                        this.$scope.$apply(() => {
                            this.setDuration();
                        });
                        return;
                    }

                    $cell.addClass('hover hover-' + this.nextDateToSet);
                    if (this.nextDateToSet === 'start') {
                        this.shadowStartDate = date;
                        this.shadowEndDate = undefined;
                    } else {
                        this.shadowEndDate = date;
                        this.shadowStartDate = this.localStartDate;
                    }
                    if (this.shadowStartDate && this.shadowEndDate) {
                        this.durations.length === 0 && this.highlightShadowDates();
                        this.$scope.$apply(() => {
                            this.setDuration();
                        });
                    }
                });
            }

            this.elements.datepickerEl.on('mouseleave.datetangepicker', 'td.drp-date', (event) => {
                let $cell = $(event.currentTarget);
                $cell.removeClass('hover');
            });

            // reset shadow dates when calendar loses hover focus
            this.elements.datepickerEl.on('mouseleave.datetangepicker', 'table.ui-datepicker-calendar', (event) => {
                this.resetShadowDates();
                this.$scope.$apply(() => {
                    this.setDuration();
                });
            });

            // close if overlay is clicked
            this.elements.overlay.on('click.datetangepicker', (event) => {
                this.$scope.$apply(() => {
                    this.closeClicked(null);
                });
            });

            this.elements.stage.on('click.datetangepicker', (event) => {
                event.stopPropagation();
            });

            this.$scope.$on('$destroy', () => {
                // unbind event listeners by namespace
                this.elements.datepickerEl.off('.datetangepicker');
                this.elements.overlay.off('.datetangepicker');
                this.elements.stage.off('.datetangepicker');
                this.html.off('.datetangepicker');
                $(window).unbind('resize');
            });

        }

        highlightShadowDates () : void {
            this.elements.datepickerEl.find('.drp-date:not(.ui-datepicker-unselectable)').each((i, val) => {
                let $cell = $(val);
                let date = this.getDateFromCalendarCell($cell);
                let isInShadow = this.shadowStartDate !== undefined
                    && this.shadowEndDate !== undefined
                    && (date >= this.shadowStartDate)
                    && (date <= this.shadowEndDate);
                $cell.toggleClass('in-shadow-range', isInShadow);
            });
        }

        getDateFromCalendarCell ($td): Date {
            // get the date from the css class we set previously
            let match = $td.attr('class').match(/drp-date-\S+/),
                date = match[0].replace('drp-date-', '');
            return this.dateUtil.parseDate(date);
        }

        setDuration () : void {
            let start = this.localStartDate ? this.localStartDate : this.shadowStartDate,
                end = this.localEndDate ? this.localEndDate : this.shadowEndDate;
            this.duration = start && end ? this.dateUtil.numberOfNights(start, end) : 0;
        }

        isStartDateEnabled (date: Date) : boolean {
            let enabled: boolean;
            if (this.enabledDates && Object.keys(this.enabledDates).length > 0) {
                const dateStr = this.dateUtil.format(date, 'yyyy-MM-dd');
                enabled = !_.isUndefined(this.enabledDates[dateStr]);
            } else {
                enabled = true;
            }
            return enabled;
        }

        applyDatePicker (element): void {

            const options = {
                numberOfMonths: 2,
                dateFormat: 'dd-M-yy',
                firstDay: 1,
                isRTL: TRPCONFIG.Rtl,
                prevText: '',
                nextText: '',
                onSelect: () => {
                    this.$scope.$apply(() => {
                        const selectedDate = this.datePicker('getDate');
                        // a date has been selected on the calendar
                        if (this.nextDateToSet === 'start') {
                            this.localStartDate = selectedDate;
                            // clear the end date
                            this.localEndDate = undefined;
                        } else if (this.nextDateToSet === 'end') {
                            this.localEndDate = selectedDate;
                        }

                        this.nextDateToSet = this.oneWay || this.nextDateToSet === 'end' ? 'start' : 'end';
                        if (this.nextDateToSet === 'start') {
                            // a range has now been selected
                            this.setRange();
                            // timeout allows model to update
                            this.$timeout(() => this.select());
                        }
                    });
                },
                beforeShowDay: date => {
                    let selectable: boolean;

                    // tag this cell with a class denoting the serialised date
                    let classes: string[] = [
                        'drp-date',
                        'drp-date-' + this.dateUtil.serialiseDate(date)
                    ];
                    if (this.nextDateToSet === 'start') {
                        // is this a permitted start date?
                        selectable = this.isStartDateEnabled(date);
                    } else {
                        // is this a permitted end date?
                        selectable = this.localStartDate !== undefined && date > this.localStartDate;
                        if (selectable && this.durations.length > 0) {
                            let duration = this.dateUtil.numberOfNights(this.localStartDate, date);
                            selectable = _.includes(this.durations, duration);
                            selectable && classes.push('valid-duration');
                        }
                    }

                    // set range highlights
                    let isRangeSet = this.localStartDate && this.localEndDate,
                        isDateInRange = isRangeSet
                            && this.localStartDate !== undefined
                            && this.localEndDate !== undefined
                            && (date >= this.localStartDate)
                            && (date <= this.localEndDate);
                    if (isDateInRange) {
                        classes.push('in-range');
                    }
                    if (isDateInRange || (!isRangeSet && this.localStartDate)) {
                        if (this.dateUtil.sameDay(date, this.localStartDate)) {
                            classes.push('start-date');
                        }
                    }
                    if (isDateInRange || (!isRangeSet && this.localEndDate)) {
                        if (this.dateUtil.sameDay(date, this.localEndDate)) {
                            classes.push('end-date');
                        }
                    }
                    return [selectable, classes.join(' ')];
                }
            };

            element.datepicker(options);
            return element.datepicker.bind(element);

        }

        private setRange() {
            this.startDate = this.dateUtil.serialiseDate(this.localStartDate);
            this.endDate = this.dateUtil.serialiseDate(this.localEndDate);
        }

        open (): void {
            if (this.datePicker == null) {
                // create the datepicker if has not been previously opened
                this.datePicker = this.applyDatePicker(this.elements.datepickerEl);
            }
            // ensure the datepicker reflects our model
            this.localStartDate = this.dateUtil.parseDate(this.startDate);
            this.localEndDate = this.oneWay ? undefined : this.dateUtil.parseDate(this.endDate);
            this.nextDateToSet = 'start';
            this.datePicker('option', 'minDate', this.dateUtil.parseDate(this.minDate));
            this.datePicker('option', 'maxDate', this.dateUtil.parseDate(this.maxDate));

            // set the start date to invoke "beforeShowDay" and highlight the initial date range
            this.datePicker('setDate', this.localStartDate);

            this.animationUtil.lockScroll();
            this.elements.overlay.addClass('fade-in');

            this.html.on('keyup.datetangepicker', (event) => {
                // ESCAPE to close
                if (event.which === 27 && this.show) {
                    this.$scope.$apply(() => {
                        this.closeClicked(null);
                    });
                }
            });
        }

        close (): void {
            this.animationUtil.restoreScroll();
            this.elements.overlay.removeClass('fade-in');
            this.html.off('keyup.datetangepicker');
        }

        closeClicked (event): void {
            if ( event ) {
                event.preventDefault();
            }
            this.show = false;
            $(window).unbind('resize');
        }

        private resetShadowDates(): void {
            this.shadowStartDate = undefined;
            this.shadowEndDate = undefined;
            this.highlightShadowDates();
        }

        public resetRange(event) {
            event.preventDefault();
            this.localStartDate = undefined;
            this.localEndDate = undefined;
            this.setRange();
            this.nextDateToSet = 'start';
            this.duration = 0;
            this.datePicker('setDate', null);
        }
    }

    angular
        .module('search')
        .directive('dateRangePicker', (): ng.IDirective => {
            return {
                restrict: 'E',
                scope: {},
                replace: true,
                controller: DateRangePicker,
                controllerAs: 'drp',
                bindToController: {
                    startDate: '=',
                    endDate: '=',
                    minDate: '=',
                    maxDate: '=',
                    enabledDates: '=',
                    select: '&',
                    show: '=',
                    durations: '=',
                    oneWay: '=',
                },
                templateUrl: 'partials/search/daterangepicker',
                link: (
                    scope: ng.IScope,
                    el: ng.IAugmentedJQuery,
                    attr: ng.IAttributes,
                    controller : DateRangePicker
                ) => {
                    const elements = {
                        overlay: el,
                        stage: el.find('.date-range-picker'),
                        datepickerEl: el.find('.datepicker-parent')
                    };
                    controller.init(elements);
                }
            };
        });
};
